import { qoursesApiInstructor } from '@/api/qourses-instructor.tsx'
import { qoursesApi } from '@/api/qourses.tsx'
import {
  courseIdParam,
  imageEditModeParam,
  meetingIdParam,
  multipassProductIdParam,
  organizationIdParam,
  tagIdParam,
} from '@/pages/public/booking/SessionParams.tsx'
import * as Sentry from '@sentry/react'
import { DateTime } from 'luxon'
import { toast } from 'sonner'
import {
  BookingEntity,
  LocationEntity,
  MeetingEntity,
  PublicLocationEntity,
} from '../qourses-api-client'

export function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

export async function sleep(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms))
}

export async function minDelay<T>(promise: Promise<T>, ms: number) {
  const [p] = await Promise.all([promise, sleep(ms)])

  return p
}

export enum ToastVariant {
  Info = 'info',
  Success = 'success',
  Error = 'error',
}

export function sendNotification(
  title: string,
  subtitle: string,
  variant: ToastVariant,
  duration = 3000,
  error?: unknown,
) {
  if (variant === ToastVariant.Info) {
    toast.info(title, {
      description: subtitle,
      duration: duration,
    })
  }
  if (variant === ToastVariant.Success) {
    toast.success(title, {
      description: subtitle,
      duration: duration,
    })
  }
  if (variant === ToastVariant.Error) {
    toast.error(title, {
      description: subtitle,
      duration: duration,
    })
    Sentry.captureException(error)
  }
}

export function removePointerEventsFromBody() {
  document.body.style.pointerEvents = ''
}

export function sendGenericErrorNotification(error?: unknown) {
  if (error) {
    Sentry.captureException(error)
    console.error(error)
  }
  sendNotification(
    'Da ist etwas schiefgelaufen',
    'Bitte versuche es erneut oder kontaktiere uns, wenn das Problem weiterhin besteht.',
    ToastVariant.Error,
  )
}

export const meetingIsInPast = (meeting: MeetingEntity) => {
  return DateTime.fromISO(meeting.end) < DateTime.now()
}

export const bookingIsInactive = (booking: BookingEntity) => {
  return (
    booking.bookingStatus === BookingEntity.bookingStatus.CANCELLED_REFUNDED ||
    booking.bookingStatus === BookingEntity.bookingStatus.REVOKED ||
    booking.bookingStatus === BookingEntity.bookingStatus.WITHDRAWN
  )
}

export const getImageUrl = (cloudflareImageId: string) => {
  const environment = import.meta.env.VITE_BASE_URL
  const accountHash = import.meta.env.VITE_CLOUDFLARE_IMAGES_ACCOUNT_HASH

  if (environment.includes('localhost') || environment.includes('qourses.top')) {
    return `https://imagedelivery.net/${accountHash}/${cloudflareImageId}/public`
  } else {
    return `/cdn-cgi/imagedelivery/${accountHash}/${cloudflareImageId}/public`
  }
}

interface BookingLinkOptions {
  organizationId?: string
  organizationSlug?: string
  courseId?: string
  meetingId?: string
  tagId?: string
  multipassProductId?: string
  includeDetails?: boolean
  includeLocation?: boolean
  includeInstructor?: boolean
  imageEditMode?: boolean
}

function createBookingURL(options: BookingLinkOptions): string {
  const environment = import.meta.env.VITE_BASE_URL
  const {
    organizationSlug,
    organizationId,
    courseId,
    meetingId,
    tagId,
    multipassProductId,
    imageEditMode = false,
  } = options

  let params

  if (!!organizationId) {
    params = new URLSearchParams({ [organizationIdParam]: organizationId })
  } else {
    params = new URLSearchParams()
  }

  if (courseId) params.append(courseIdParam, courseId)
  if (meetingId) params.append(meetingIdParam, meetingId)
  if (tagId) params.append(tagIdParam, tagId)
  if (multipassProductId) params.append(multipassProductIdParam, multipassProductId)
  if (imageEditMode) params.append(imageEditModeParam, 'true')

  // Create shortened link for organization or courses
  if (organizationSlug) {
    // Check if we should use the short link format
    if (
      !meetingId &&
      !tagId &&
      !multipassProductId &&
      !imageEditMode &&
      (courseId || (!courseId && !meetingId && !tagId && !multipassProductId && !imageEditMode))
    ) {
      let shortPath = `/s/${organizationSlug}`

      // If courseId exists, add the shortened course path
      if (courseId) {
        // Convert UUID to a shorter format if needed
        const shortCourseId = shortenUuid(courseId)
        shortPath += `/c/${shortCourseId}`
      }

      if (environment.includes('localhost')) {
        return `http://localhost:3002${shortPath}`
      } else {
        // path to shrty
        return `https://${window.location.host.split('.').slice(-2).join('.')}${shortPath}`
      }
    }
  }

  // Default to regular booking link if not using short links
  return `${window.location.origin}/book?${params.toString()}`
}

/**
 * Shortens a UUID to make it more URL-friendly while still being reversible
 * This implementation uses Base62 encoding to shorten the UUID
 */
function shortenUuid(uuid: string): string {
  // Remove hyphens from the UUID
  const cleanUuid = uuid.replace(/-/g, '')

  // Convert the hex UUID to a shorter Base62 representation
  // This is a simplified implementation - you may want to use a proper base conversion library
  return hexToBase62(cleanUuid)
}

/**
 * Converts a hexadecimal string to Base62 (0-9, a-z, A-Z)
 * Base62 makes the URL shorter and more readable than hexadecimal
 */
function hexToBase62(hex: string): string {
  // Convert hex to decimal number (as BigInt since UUIDs are large)
  const decimal = BigInt('0x' + hex)

  // Base62 character set: 0-9, a-z, A-Z
  const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
  let result = ''

  // Convert decimal to Base62
  let value = decimal

  // Handle case where value is 0
  if (value === 0n) return '0'

  while (value > 0n) {
    const remainder = Number(value % 62n)
    result = chars[remainder] + result
    value = value / 62n
  }

  return result
}

export function copyBookingLinkToClipboard(options: BookingLinkOptions): string {
  const url = createBookingURL(options)
  navigator.clipboard.writeText(url)
  return url
}

export function createBookingLink(options: BookingLinkOptions & { newTab?: boolean }): string {
  const { newTab, ...urlOptions } = options
  const url = createBookingURL(urlOptions)

  if (newTab) {
    window.open(url, '_blank')
  }

  return url
}

export function addressString(
  street: string | null,
  streetNumber: string | null,
  postalCode: string | null,
  city: string | null,
  country: string | null,
) {
  const parts = [
    street,
    streetNumber,
    [postalCode, city].filter(Boolean).join(' '),
    country,
  ].filter(Boolean)

  return parts.join(', ')
}

export function addressStringFromLocation(location: LocationEntity | PublicLocationEntity) {
  return addressString(
    location.addressLine1,
    location.addressLine2,
    location.postalCode,
    location.addressLevel2,
    location.addressLevel3,
  )
}

export const redirectToStripeOnboarding = async (
  notificationTitle: string,
  notificationSubtitle: string,
) => {
  sendNotification(notificationTitle, notificationSubtitle, ToastVariant.Info)

  const onboarding = await minDelay(
    qoursesApi.payment.paymentControllerGetStripePaymentProfileOnboardingV1('false'),
    2000,
  )

  window.location.href = onboarding.url
}

export const redirectToStripeOnboardingInstructor = async (
  notificationTitle: string,
  notificationSubtitle: string,
) => {
  sendNotification(notificationTitle, notificationSubtitle, ToastVariant.Info)

  const onboarding = await minDelay(
    qoursesApiInstructor.payment.paymentControllerGetStripePaymentProfileOnboardingV1(),
    2000,
  )

  window.location.href = onboarding.url
}

export function getRandomEmoji() {
  const emojis = [
    '🏄',
    '🦄',
    '🐙',
    '🐬',
    '🌈',
    '🍔',
    '🍪',
    '🍭',
    '🌿',
    '🦐',
    '🎮',
    '🕹️',
    '🧁',
    '🥑',
    '🍕',
    '🚀',
    '👾',
    '🎉',
    '🔮',
    '🦩',
    '🍉',
    '🎈',
    '🧩',
    '🛹',
    '🍿',
    '🥳',
    '🐉',
    '🍜',
    '🍣',
    '🪁',
  ]
  return emojis[Math.floor(Math.random() * emojis.length)]
}
